/**
 * Signal bars (0–5) from the device's own modem signal metrics, phone-style. Only metrics whose scale the metric
 * catalog defines are used, in this order (docs/architecture/07-telemetry.md "Signal bars"):
 *
 *   network.rsrp_dbm   LTE RSRP in dBm:      ≥ −85 → 5, ≥ −95 → 4, ≥ −105 → 3, ≥ −115 → 2, else 1
 *   network.signal     CSQ index 0–31:       ≥ 20 → 5, ≥ 15 → 4, ≥ 10 → 3, ≥ 5 → 2, else 1; > 31 = no signal
 *   network.signal_pct percentage 0–100:     ≥ 60 → 5, ≥ 45 → 4, ≥ 30 → 3, ≥ 15 → 2, else 1
 *
 * The percentage bands are the CSQ bands × 3 (a device reporting 3 × CSQ gets the same bars as one reporting CSQ).
 * Link-radio values an upstream device observes (`vendor.*`) and RSSI/SNR of undocumented scale are not used.
 */
export type SignalSource = 'network.rsrp_dbm' | 'network.signal' | 'network.signal_pct';
export type SignalLevel = { bars: number; source: SignalSource; value: number };

export const SIGNAL_SOURCES: readonly SignalSource[] = [
  'network.rsrp_dbm',
  'network.signal',
  'network.signal_pct',
];

const BANDS: Record<SignalSource, readonly [number, number, number, number]> = {
  'network.rsrp_dbm': [-85, -95, -105, -115],
  'network.signal': [20, 15, 10, 5],
  'network.signal_pct': [60, 45, 30, 15],
};

const bars = (v: number, [b5, b4, b3, b2]: readonly [number, number, number, number]) =>
  v >= b5 ? 5 : v >= b4 ? 4 : v >= b3 ? 3 : v >= b2 ? 2 : 1;

/** Bars for one metric value; 0 = reported as "no signal", undefined = value outside the metric's scale. */
export function signalBars(source: SignalSource, v: number): number | undefined {
  if (!Number.isFinite(v)) return undefined;
  if (source === 'network.signal') {
    if (v === 99) return 0; // CSQ "not known or not detectable"
    if (v < 0 || v > 31) return undefined;
  }
  if (source === 'network.signal_pct' && (v < 0 || v > 100)) return undefined;
  if (source === 'network.rsrp_dbm' && (v < -140 || v > -44)) return undefined;
  return bars(v, BANDS[source]);
}

/** The first usable signal metric of a device, or undefined when it reports none. */
export function signalLevel(metrics: Record<string, number> | undefined): SignalLevel | undefined {
  if (!metrics) return undefined;
  for (const source of SIGNAL_SOURCES) {
    const value = metrics[source];
    if (value === undefined) continue;
    const b = signalBars(source, value);
    if (b !== undefined) return { bars: b, source, value };
  }
  return undefined;
}
